// import React, { useState, useEffect } from 'react'; // Remove React if unused
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusIcon, UserPlusIcon, CheckIcon, XMarkIcon, TrashIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { Game, CollectionDetailData as Collection, Member, MyPendingCollectionInvite, Player } from '../types';

export default function CollectionDetail() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotification();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [myPendingInvite, setMyPendingInvite] = useState<MyPendingCollectionInvite | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddingGame, setIsAddingGame] = useState(false);
  const [newGame, setNewGame] = useState({ name: '' });
  const [isInvitingUser, setIsInvitingUser] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  // Action Loading States
  const [isSubmittingGame, setIsSubmittingGame] = useState(false);
  const [isDeletingGameId, setIsDeletingGameId] = useState<string | null>(null);
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [isDecliningInvite, setIsDecliningInvite] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null); // Store member user_id
  const [isRemovingMember, setIsRemovingMember] = useState<string | null>(null); // Store member user_id
  const [isLeaving, setIsLeaving] = useState(false);
  const [isAddingMyselfAsPlayer, setIsAddingMyselfAsPlayer] = useState(false);

  useEffect(() => {
    if (collectionId) {
      fetchCollection();
      fetchGames();
      fetchMembers();
      fetchMyInvites();
      fetchPlayers();
    }
  }, [collectionId]);

  // Debugging useEffect - add this near the top of the component
  useEffect(() => {
    if (collection && user && players.length > 0) {
      console.log("Collection Detail Debug:");
      console.log("- User:", user.username, "(ID:", user.id, ")");
      console.log("- Is Guest:", user.isGuest);
      console.log("- Is already player:", players.some(p => p.user_id === user.id));
      console.log("- Player list:", players.map(p => ({ id: p.id, name: p.name, user_id: p.user_id })));
    }
  }, [collection, user, players]);

  const fetchCollection = async () => {
    try {
      const response = await fetch(`/api/collections/${collectionId}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch collection');

      const data = await response.json() as Collection;
      setCollection(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGames = async () => {
    try {
      const response = await fetch(`/api/collections/${collectionId}/games`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch games');

      const data = await response.json() as Game[];
      setGames(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const fetchMembers = async () => {
    if (!collectionId) return;
    try {
      const response = await fetch(`/api/collections/${collectionId}/members`, {
        credentials: 'include',
      });
      if (response.status === 403) {
        // User might not be a member, handle gracefully
        setError('You do not have permission to view members for this collection.');
        setMembers([]);
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch members');
      const data = await response.json() as Member[];
      setMembers(data);
    } catch (err) {
      console.error('Fetch members error:', err);
      // Don't overwrite main error if collection fetch failed
      if (!error) setError(err instanceof Error ? err.message : 'Failed to load members');
      setMembers([]);
    }
  };

  const fetchMyInvites = async () => {
    if (!collectionId) return;
    try {
      // Fetch ALL pending invites for the user
      const response = await fetch(`/api/me/collection-invites`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch pending invites');
      const allInvites = await response.json() as MyPendingCollectionInvite[];
      
      // Find the invite specifically for *this* collection
      const relevantInvite = allInvites.find(invite => invite.collection_id === collectionId);
      setMyPendingInvite(relevantInvite || null);
      
    } catch (err) {
       console.error('Fetch my invites error:', err);
      // Don't overwrite main error
      if (!error) setError(err instanceof Error ? err.message : 'Failed to check invites');
       setMyPendingInvite(null);
    }
  };

  const fetchPlayers = async () => {
    if (!collectionId) return;
    try {
      console.log("Fetching players for collection:", collectionId);
      console.log("Current user:", user?.id, user?.username, "Collection role:", collection?.userRole);
      console.log("Are you the collection owner?", collection?.owner_id === user?.id);
      
      const response = await fetch(`/api/collections/${collectionId}/players`, {
        credentials: 'include',
      });
      console.log("Players API response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        console.error("Players API error:", errorData);
        throw new Error(`Failed to fetch players: ${response.status} ${errorData.error || ''}`);
      }
      
      const data = await response.json() as Player[];
      console.log("Players fetched successfully:", data.length, "players");
      setPlayers(data);
    } catch (err) {
      console.error('Fetch players error:', err);
      // Don't overwrite main error
      if (!error) setError(err instanceof Error ? err.message : 'Failed to load players');
      setPlayers([]);
    }
  };

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmittingGame(true);
    try {
      const response = await fetch(`/api/collections/${collectionId}/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newGame),
      });

      if (!response.ok) throw new Error('Failed to create game');

      const data = await response.json() as Game;
      setGames(prev => [...prev, data]);
      setCollection(prev => prev ? {
        ...prev,
        game_count: prev.game_count + 1
      } : null);
      setIsAddingGame(false);
      setNewGame({ name: '' });
      addNotification('Game created!', 'success');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsSubmittingGame(false);
    }
  };

  const handleDeleteGame = async (gameId: string) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    setIsDeletingGameId(gameId);
    try {
      const response = await fetch(`/api/collections/${collectionId}/games/${gameId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to delete game');

      setGames(prev => prev.filter(game => game.id !== gameId));
      setCollection(prev => prev ? {
        ...prev,
        game_count: prev.game_count - 1
      } : null);
      addNotification('Game deleted.', 'success');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsDeletingGameId(null);
    }
  };

  // --- Membership Handlers ---

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!collectionId || !inviteUsername.trim()) return;
    setError('');
    setIsSubmittingInvite(true);
    try {
      const response = await fetch(`/api/collections/${collectionId}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inviteeUsername: inviteUsername })
      });
      
      // Handle potential non-JSON responses
      let data: { error?: string; message?: string };
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("Error parsing JSON response:", jsonError);
        throw new Error(`Server error: Failed to parse response (Status: ${response.status})`);
      }
      
      if (!response.ok) throw new Error(data?.error || `Failed to send invite (${response.status})`);
      
      addNotification('Invite sent successfully!', 'success');
      setInviteUsername('');
      setIsInvitingUser(false);
      fetchMembers();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred during invite';
      console.error("Invite error:", err);
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsSubmittingInvite(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!collectionId) return;
    setError('');
    setIsAcceptingInvite(true);
    try {
      const response = await fetch(`/api/me/collection-invites/${collectionId}`, {
        method: 'PUT',
        credentials: 'include',
      });
       const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to accept invite');
      addNotification('Invite accepted!', 'success');
      setMyPendingInvite(null);
      fetchCollection();
      fetchMembers();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to accept invite';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsAcceptingInvite(false);
    }
  };
  
  const handleDeclineInvite = async () => {
    if (!collectionId) return;
     setError('');
    setIsDecliningInvite(true);
    try {
      const response = await fetch(`/api/me/collection-invites/${collectionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
       const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to decline invite');
      addNotification('Invite declined.', 'info');
      setMyPendingInvite(null);
    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'Failed to decline invite';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
      setIsDecliningInvite(false);
    }
  };

  const handleChangeRole = async (memberUserId: string, newRole: 'admin' | 'member') => {
    if (!collectionId || !collection || collection.userRole === 'member') return;
    setError('');
    setIsUpdatingRole(memberUserId);
    try {
      const response = await fetch(`/api/collections/${collectionId}/members/${memberUserId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to change role');
      addNotification('Role updated!', 'success');
      fetchMembers();
    } catch (err) {
       const errorMsg = err instanceof Error ? err.message : 'Failed to change role';
       setError(errorMsg);
       addNotification(errorMsg, 'error');
    } finally {
       setIsUpdatingRole(null);
    }
  };
  
  const handleRemoveMember = async (memberUserId: string, memberUsername: string) => {
     if (!collectionId || !collection || collection.userRole === 'member') return;
     if (!window.confirm(`Are you sure you want to remove ${memberUsername} from this collection?`)) return;
     setError('');
     setIsRemovingMember(memberUserId);
     try {
      const response = await fetch(`/api/collections/${collectionId}/members/${memberUserId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json() as { error?: string; message?: string; success?: boolean };
      if (!response.ok) throw new Error(data.error || 'Failed to remove member');
      addNotification('Member removed.', 'success');
      fetchMembers();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to remove member';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsRemovingMember(null);
    }
  };

  const handleLeaveCollection = async () => {
    if (!collectionId || !collection) return;
    if (collection.userRole === 'owner') {
      addNotification("Owners cannot leave collections directly. Transfer ownership or delete the collection.", 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to leave the collection "${collection.name}"?`)) return;
    setError('');
    setIsLeaving(true);
    try {
      const response = await fetch(`/api/me/collections/${collectionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json() as { error?: string; message?: string };
      if (!response.ok) throw new Error(data.error || 'Failed to leave collection');
      addNotification('You have left the collection.', 'success');
      navigate('/collections');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to leave collection';
      setError(errorMsg);
      addNotification(errorMsg, 'error');
    } finally {
      setIsLeaving(false);
    }
  };

  // -------------------------

  // Add SkeletonListItem definition
  const SkeletonListItem = () => (
    <li className="px-4 sm:px-6 py-4 animate-pulse">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/5 mb-1"></div>
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
    </li>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Collection not found</h2>
        <button
          onClick={() => navigate('/collections')}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Collections
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header: Stack vertically on small screens */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{collection.name}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {collection.game_count} games · {collection.player_count} players
          </p>
        </div>
        {/* Action Buttons: Adjust width/stacking if needed */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-wrap justify-end">
          {/* Add Game Button (Only if owner/admin) */}
          {(collection.userRole === 'owner' || collection.userRole === 'admin') && (
            <button
              onClick={() => setIsAddingGame(true)}
              className="btn-primary text-sm mb-2 sm:mb-0"
              disabled={isAddingGame || isInvitingUser || isLeaving || isLoading}
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Add Game
            </button>
          )}
          {/* Invite User Button (Only if owner/admin and not guest) */}
          {user && !user.isGuest && (collection.userRole === 'owner' || collection.userRole === 'admin') && (
            <button
              onClick={() => setIsInvitingUser(true)}
              className="btn-secondary text-sm mb-2 sm:mb-0"
              disabled={isAddingGame || isInvitingUser || isLeaving || isLoading}
            >
              <UserPlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Invite User
            </button>
          )}
          {/* Leave Collection Button (Only if NOT owner and NOT guest) */}
          {user && !user.isGuest && collection.userRole !== 'owner' && (
            <button
              onClick={handleLeaveCollection}
              className="btn-secondary text-sm text-red-600 border-red-300 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20 mb-2 sm:mb-0"
              title="Leave this collection"
              disabled={isLeaving || isLoading}
            >
              {isLeaving ? (
                  <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg> 
                      Leaving...
                  </span>
               ) : ( 'Leave Collection' )}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Pending Invite Banner */}
      {myPendingInvite && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-md mb-6 flex justify-between items-center">
          <div>
            You have a pending invite to join this collection (invited by {myPendingInvite.inviter_username}).
          </div>
          <div className="space-x-2">
            <button onClick={handleAcceptInvite} className="btn-primary text-sm" disabled={isAcceptingInvite || isDecliningInvite}>
              {isAcceptingInvite ? (
                  <span className="flex items-center">
                      <svg className="animate-spin h-5 w-5 mr-1 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg> 
                      Accepting...
                  </span>
              ) : (
                  <><CheckIcon className="h-5 w-5 mr-1 inline" /> Accept</>
              )}
            </button>
            <button onClick={handleDeclineInvite} className="btn-secondary text-sm" disabled={isAcceptingInvite || isDecliningInvite}>
               {isDecliningInvite ? (
                   <span className="flex items-center">
                       <svg className="animate-spin h-5 w-5 mr-1 text-gray-700 dark:text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                       </svg>
                       Declining...
                   </span>
               ) : (
                   <><XMarkIcon className="h-5 w-5 mr-1 inline" /> Decline</>
               )}
            </button>
          </div>
        </div>
      )}

      {/* Invite User Form */}
      {isInvitingUser && (collection.userRole === 'owner' || collection.userRole === 'admin') && (
        <form onSubmit={handleInviteUser} className="bg-white p-6 rounded-lg shadow-sm space-y-4 mt-6">
           <h3 className="text-lg font-medium text-gray-900">Invite User to Collection</h3>
          <div>
            <label htmlFor="inviteUsername" className="block text-sm font-medium text-gray-700">
              Username to Invite
            </label>
            <input
              type="text"
              id="inviteUsername"
              value={inviteUsername}
              onChange={(e) => setInviteUsername(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              placeholder="Enter registered username"
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsInvitingUser(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!inviteUsername.trim() || isSubmittingInvite}
            >
              {isSubmittingInvite ? (
                  <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg> 
                      Sending...
                  </span>
               ) : ( 'Send Invite' )}
            </button>
          </div>
        </form>
      )}

      {isAddingGame && (
        <form onSubmit={handleCreateGame} className="bg-white p-6 rounded-lg shadow-sm space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Game Name
            </label>
            <input
              type="text"
              id="name"
              value={newGame.name}
              onChange={(e) => setNewGame(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsAddingGame(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!newGame.name.trim() || isSubmittingGame}
            >
              {isSubmittingGame ? (
                  <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg> 
                      Creating...
                  </span>
               ) : ( 'Create' )}
            </button>
          </div>
        </form>
      )}

      {/* Games Grid */}
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mt-8">Games</h2>
      <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          // Show skeleton cards while loading
          [...Array(3)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="flex justify-between items-start mb-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/5"></div>
                <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
              </div>
              <div className="mt-auto pt-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              </div>
            </div>
          ))
        ) : (
          // Render actual collection cards
          games.map((game) => (
            <div
              key={game.id}
              className="card p-4 flex flex-col justify-between cursor-pointer" // Use flex column
              onClick={() => navigate(`/games/${game.id}`)}
            >
              <div> {/* Content wrapper */} 
                <div className="flex justify-between items-start mb-2"> {/* Align items start */} 
                  <div className="flex items-center space-x-2">
                     <PuzzlePieceIcon className="h-6 w-6 text-primary dark:text-primary-light opacity-75" />
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {game.name}
                    </h3>
                  </div>
                 {collection && (collection.userRole === 'owner' || collection.userRole === 'admin') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent navigation
                        // Add confirmation prompt for delete
                        if (window.confirm(`Are you sure you want to delete the game "${game.name}"? This cannot be undone.`)) {
                            handleDeleteGame(game.id);
                        }
                      }}
                      className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Delete Game"
                      disabled={isDeletingGameId === game.id}
                    >
                      {isDeletingGameId === game.id ? (
                          <svg className="animate-spin h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                      ) : (
                          <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-auto pt-2"> {/* Push date to bottom */} 
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  Created {new Date(game.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))
        )}
        {!isLoading && games.length === 0 && (
           <div className="col-span-full text-center py-12">
             <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No games in this collection yet</h3>
             {collection && (collection.userRole === 'owner' || collection.userRole === 'admin') && (
                <p className="mt-2 text-gray-500 dark:text-gray-400">
                  Click "Add Game" to create the first one!
                </p>
             )}
          </div>
        )}
      </div>

      {/* Members Section */}
      <div className="mt-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Members</h2>
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              // Use isLoading for Members list skeleton
              [...Array(4)].map((_, i) => <SkeletonListItem key={i} />)
            ) : members.length === 0 ? (
              <li className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">No members yet.</li>
            ) : (
              members.map((member) => (
                <li key={member.user_id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900">{member.username}</span>
                    <span className={`text-sm ${member.status === 'pending' ? 'text-yellow-600' : 'text-gray-500'}`}>
                      {member.role} ({member.status})
                    </span>
                  </div>
                  
                  {/* Controls: Ensure they don't wrap awkwardly */} 
                  {(collection.userRole === 'owner' || collection.userRole === 'admin') && member.user_id !== collection.owner_id && ( 
                    <div className="flex items-center space-x-2 self-end sm:self-center">
                      {/* Role Changer - Show only for active members */} 
                      {member.status === 'active' && (
                         <select 
                            value={member.role}
                            onChange={(e) => handleChangeRole(member.user_id, e.target.value as 'admin' | 'member')} 
                            className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
                            // Disable changing role to/from owner for simplicity 
                            disabled={isUpdatingRole === member.user_id || isRemovingMember === member.user_id || member.role === 'owner' || collection.userRole === 'admin' && member.role === 'admin'}
                         >
                           {/* <option value="owner" disabled>Owner</option> */} {/* Don't allow promoting to owner */} 
                           <option value="admin">Admin</option>
                           <option value="member">Member</option>
                         </select>
                      )}
                      
                      {/* Remove/Cancel Button */} 
                      <button 
                        onClick={() => handleRemoveMember(member.user_id, member.username)} 
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100"
                        title={member.status === 'pending' ? 'Cancel Invite' : 'Remove Member'}
                        disabled={isUpdatingRole === member.user_id || isRemovingMember === member.user_id}
                      >
                        {isRemovingMember === member.user_id ? (
                            <svg className="animate-spin h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <TrashIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* Add Players Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Players</h2>
          
          {/* Show "Add Myself as Player" button if user is logged in and a collection member */}
          {user && !user.isGuest && collection && (
            <div className="flex gap-2">
              {/* Only show if the user isn't already a player in this collection */}
              {!players.some(player => player.user_id === user.id) && (
                <button
                  onClick={() => {
                    // Set loading state
                    setIsAddingMyselfAsPlayer(true);
                    setError('');
                    
                    // Make API request to add the current user as a player
                    console.log("CollectionDetail: Adding myself as player, making request with:", {
                      name: user.username,
                      playerUserId: user.id,
                      url: `/api/collections/${collectionId}/players`
                    });

                    fetch(`/api/collections/${collectionId}/players`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include', 
                      body: JSON.stringify({
                        name: user.username,
                        playerUserId: user.id
                      })
                    })
                    .then(response => {
                      console.log("Add myself response status:", response.status);
                      if (!response.ok) {
                        return response.json().then(data => {
                          console.error("Add myself error:", data);
                          throw new Error((data as {error?: string}).error || 'Failed to add yourself as player');
                        });
                      }
                      return response.json();
                    })
                    .then(data => {
                      console.log("Add myself success:", data);
                      // Success!
                      addNotification('You were added as a player!', 'success');
                      // Refresh players and collection data
                      fetchPlayers();
                      fetchCollection();
                    })
                    .catch(err => {
                      console.error("Add myself exception:", err);
                      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
                      setError(errorMsg);
                      addNotification(errorMsg, 'error');
                    })
                    .finally(() => {
                      setIsAddingMyselfAsPlayer(false);
                      console.log("Add myself operation complete");
                    });
                  }}
                  className="btn-secondary text-sm"
                  disabled={isAddingMyselfAsPlayer}
                >
                  {isAddingMyselfAsPlayer ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700 dark:text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </span>
                  ) : (
                    "Add Myself as Player"
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Player list */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              // Use isLoading for Players list skeleton
              [...Array(4)].map((_, i) => <SkeletonListItem key={i} />)
            ) : players.length === 0 ? (
              <li className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">No players yet.</li>
            ) : (
              players.map((player) => (
                <li key={player.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 dark:text-white">{player.name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {player.is_guest ? 'Guest' : 'Registered'} 
                      {player.username && ` (${player.username})`}
                    </span>
                  </div>
                  {/* Optional: Add buttons here to manage players if needed */}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
      
    </div>
  );
} 